<template>
  <div class="item-container">
    <div v-if="isDragTarget" class="drag-overlay">
      {{ dragMessage }}
    </div>

    <div class="md-elevation-3 item-handle" :class="{
      'item-handle-disabled': isDisabled,
      'item-handle-off': !item.options.enabled && (!isDisabled || state.hasFocus),
      'item-handle-on': item.options.enabled && (!isDisabled || state.hasFocus),
    }"
    >
      <span>{{ positionLabel }}.</span>
      <md-icon class="drag-icon" :class="{
        'draggable': !isDisabled || isDragTarget,
        'drag-icon-disabled': isDisabled,
        'drag-icon-off': !item.options.enabled && (!isDisabled || state.hasFocus),
      }"
      >
        drag_indicator
      </md-icon>
    </div>

    <div class="field-container">
      <md-field v-if="!settings.isQuestionDisabled" :md-counter="true">
        <label>{{ itemLabel }}</label>
        <md-input
          ref="questionInput"
          :value="item.content.en"
          :class="{ error: !item.content.en }"
          :disabled="readonly"
          :maxlength="256"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter.prevent="handleQuestionEnter"
          @input="(value) => editQuestion(value)"
        />
        <md-checkbox
          v-if="isChild && !item.isNew"
          class="off-at-start-checkbox"
          :value="!item.options.enabled"
          @change="editDisabled"
        >
          Off at start
        </md-checkbox>
        <help-tooltip
          v-if="warnings"
          type="warning"
          direction="top"
          class="warning-tooltip"
        >
          <span v-for="warning in warnings" :key="warning.message">{{ warning.message }}<br></span>
        </help-tooltip>
      </md-field>

      <md-field v-if="!settings.isAnswerDisabled" :md-counter="true">
        <label>{{ item.isNew ? settings.newAnswerLabel : settings.answerLabel }}</label>
        <md-input
          ref="answerInput"
          v-model="item.options.answer.en"
          class="answer-input"
          :class="{ warning: !item.options.answer.en }"
          :disabled="readonly"
          :maxlength="256"
          @focus="handleFocus"
          @blur="handleBlur"
          @keyup.enter.prevent="handleAnswerEnter"
          @input="(value) => editAnswer(value)"
        />

        <div v-if="!isDisabled">
          <help-tooltip
            v-if="!item.options.answer.en"
            type="warning"
            direction="top"
            class="warning-tooltip"
          >
            You're missing an answer
            <br>for this question
          </help-tooltip>

          <md-button
            v-else
            :md-ripple="false"
            class="md-icon-button md-dense play-answer-button"
            tabindex="-1"
            @click="playAnswerText()"
          >
            <md-icon>volume_up</md-icon>
          </md-button>
        </div>
      </md-field>
    </div>

    <div class="counter-placeholder" />

    <md-button
      :disabled="isDisabled"
      class="md-raised md-primary md-icon-button"
      tabindex="-1"
      @click="remove()"
    >
      <md-icon>remove</md-icon>
    </md-button>
  </div>
</template>

<script>
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';

export default {
  name: 'dialogue-item',
  components: {
    HelpTooltip,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    isChild: {
      type: Boolean,
      default: false,
    },
    warnings: {
      type: Array,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    dragMessage: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      state: {
        hasFocus: false,
      }
    };
  },
  computed: {
    isDisabled() {
      return this.readonly || this.item.isNew;
    },
    isDragTarget() {
      return this.item.isNew && this.dragMessage;
    },
    itemLabel() {
      if (this.item.isNew) {
        return this.isChild ? this.settings.newChildLabel : this.settings.newQuestionLabel;
      } else {
        return this.isChild ? this.settings.childLabel : this.settings.questionLabel;
      }
    },
    positionLabel() {
      return this.isChild ? String.fromCharCode('A'.charCodeAt(0) + this.item.options.position) : this.item.options.position + 1;
    },
  },
  created() {
    this.$eventHub.$on('DIALOGUE::ENTER', payload => {
      if (this.settings.type === payload.type && this.item.options.position === (payload.position + 1)) {
        if (this.settings.isQuestionDisabled) {
          this.focusAnswer();
        } else {
          this.focusQuestion();
        }
      }
    });
  },
  methods: {
    editQuestion(value) {
      this.$emit('editQuestion', this.item, value);
    },
    handleFocus() {
      this.state.hasFocus = true;
    },
    handleBlur() {
      this.state.hasFocus = false;
    },
    editAnswer(value) {
      this.$emit('editAnswer', this.item, value);
    },
    handleQuestionEnter() {
      if (this.settings.isAnswerDisabled) {
        // Send focus to next dialogue item
        this.$eventHub.$emit('DIALOGUE::ENTER', { type: this.settings.type, position: this.item.options.position});
      } else {
        this.focusAnswer();
      }
    },
    handleAnswerEnter() {
      // Send focus to next dialogue item
      this.$eventHub.$emit('DIALOGUE::ENTER', { type: this.settings.type, position: this.item.options.position});
    },
    focusQuestion() {
      if (this.$refs.questionInput) {
        this.$refs.questionInput.$el.focus();
      }
    },
    focusAnswer() {
      if (this.$refs.answerInput) {
        this.$refs.answerInput.$el.focus();
      }
    },
    playAnswerText() {
      this.$emit('playAudio', this.item);
    },
    remove() {
      this.$emit('remove', this.item);
    },
    editDisabled(value) {
      const enabled = !value; // invert for data model
      this.$emit('editEnabled', this.item, enabled);
    }
  },
}
</script>

<style scoped>
.item-container {
  position: relative;
  padding: 6px;
  padding-left: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.drag-overlay {
  /* opaque overlay to hide content */
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background-color: #f3f3f3;
  z-index: 6;
  padding: 10px;
  outline: 3px dashed lightblue;
  outline-offset: -8px;
  text-align: center;
  color: lightblue;
}

.item-handle {
  padding: 4px 6px;
  margin-left: 4px;
  font-size: 12px;
  border-radius: 3px;
  align-self: stretch;
  position: relative;
  width: 60px;
}

.item-handle-disabled {
  color: rgba(0, 0, 0, 0.54);
  background-color: #e8e8e8;
}

.item-handle-off {
  color: white;
  background-color: black;
}

.item-handle-on {
  background-color: white;
}

.item-handle-off {
  color: white !important;
}

.field-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.drag-icon {
  width: 50px;
  height: 50px;
  position: absolute;
  top: 50%;
  left: 50%;
  margin: -25px 0 0 -25px; /* apply negative top and left margins to truly center the element */
}

.drag-icon-disabled {
  color: rgba(0, 0, 0, 0.54) !important;
}

.drag-icon-off {
  color: white !important;
}

.draggable {
  cursor: pointer;
}

.warning-tooltip {
  position: absolute;
  right: 10px;
}

.answer-input {
  padding-right: 40px;
}

.play-answer-button {
  position: absolute;
  right: 4px;
}

.md-field {
  flex: 1;
  margin: 0;
  padding: 0;
  min-height: 0;
}

.md-field >>> .md-count {
  right: -50px;
  bottom: 0;
}

.counter-placeholder {
  width: 80px;
}

.off-at-start-checkbox {
  margin: 0;
  margin-right: 8px;
  padding-left: 8px;
}

.off-at-start-checkbox >>> label {
  left: 0 !important;
}
</style>